import { useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { v4 as uuidv4 } from 'uuid'
import { Check, ChevronsUpDown, Globe } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { isReservedStatusName } from '../constants/reservedStatusNames'
import type { StatusDocument } from '@/lib/db/rxdb/schemas/status'
import type { ProjectDocument } from '@/lib/db/rxdb/hooks'
import type { ProgressType } from '@/lib/types/subscription'
import { useProjects, useStatusMutations } from '@/lib/db/rxdb/hooks'
import { useStatuses } from '@/routes/features/tasks/hooks/useWorkspaceStatuses'
import useLoginStore from '@/store/LoginStore'
import { PROGRESS_TYPES } from '@/lib/types/subscription'
import { generateInitialRank, generateNextRank } from '@/lib/utils/lexorank'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { cn } from '@/lib/utils'
import { logger } from '@/lib/monitoring/logger'
import { LogCategory } from '@/lib/monitoring/types'
import { groupProjectsBySpace } from '@/lib/utils/groupProjectsBySpace'
import { ProjectIcon } from '@/lib/utils/projectIcon'

const PROGRESS_TYPE_VALUES = [
  PROGRESS_TYPES.BACKLOG,
  PROGRESS_TYPES.PLANNED,
  PROGRESS_TYPES.IN_PROGRESS,
  PROGRESS_TYPES.COMPLETED,
  PROGRESS_TYPES.CANCELLED,
] as const

const makeStatusFormSchema = (t: (key: string) => string) =>
  z.object({
    name: z
      .string()
      .trim()
      .min(1, t('status_management.errors.name_required'))
      .max(50, t('status_management.errors.name_required'))
      .refine((value) => !isReservedStatusName(value), {
        message: t('status_management.errors.name_reserved'),
      }),
    progressType: z.enum(PROGRESS_TYPE_VALUES),
    description: z.string().max(200),
    projectIds: z.array(z.string()),
  })

type StatusFormValues = z.infer<ReturnType<typeof makeStatusFormSchema>>

export interface StatusFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  mode: 'create' | 'edit'
  initialStatus?: StatusDocument
  workspaceId: string
  /**
   * Optional seed scope for create mode. Used by the project-level
   * "Create project-only status" flow to pre-select the current project in
   * the scope picker. Ignored in edit mode (where `initialStatus.projectIds`
   * is the source of truth).
   */
  initialProjectIds?: Array<string>
}

export function StatusFormDialog({
  open,
  onOpenChange,
  mode,
  initialStatus,
  workspaceId,
  initialProjectIds,
}: StatusFormDialogProps) {
  const { t } = useTranslation('settings')
  const userId = useLoginStore((state) => state.userId)
  const { createStatus, updateStatus } = useStatusMutations()
  const { projects } = useProjects()
  const statusHook = useStatuses()
  const allStatuses = statusHook.all.statuses

  const isSystem = Boolean(initialStatus?.isSystemGenerated)
  const isEdit = mode === 'edit'

  const statusFormSchema = useMemo(() => makeStatusFormSchema(t), [t])

  const form = useForm<StatusFormValues>({
    resolver: zodResolver(statusFormSchema),
    defaultValues: {
      name: '',
      progressType: PROGRESS_TYPES.PLANNED,
      description: '',
      projectIds: initialProjectIds ?? [],
    },
  })

  useEffect(() => {
    if (!open) return
    if (isEdit && initialStatus) {
      form.reset({
        name: initialStatus.name,
        progressType: (initialStatus.progressType as ProgressType) ?? PROGRESS_TYPES.PLANNED,
        description: initialStatus.description ?? '',
        projectIds: initialStatus.projectIds ?? [],
      })
    } else {
      form.reset({
        name: '',
        progressType: PROGRESS_TYPES.PLANNED,
        description: '',
        projectIds: initialProjectIds ?? [],
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialStatus?.id, initialProjectIds])

  const selectableProjectIds = useMemo(() => {
    const groups = groupProjectsBySpace(projects)
    return new Set(groups.flatMap((g) => g.children.map((c) => c.id)))
  }, [projects])

  const progressTypeOptions: Array<{ value: ProgressType; label: string }> = [
    { value: PROGRESS_TYPES.BACKLOG, label: t('status_management.progress_types.backlog') },
    { value: PROGRESS_TYPES.PLANNED, label: t('status_management.progress_types.planned') },
    {
      value: PROGRESS_TYPES.IN_PROGRESS,
      label: t('status_management.progress_types.in_progress'),
    },
    { value: PROGRESS_TYPES.COMPLETED, label: t('status_management.progress_types.completed') },
    { value: PROGRESS_TYPES.CANCELLED, label: t('status_management.progress_types.cancelled') },
  ]

  const onSubmit = async (data: StatusFormValues) => {
    if (!userId) return
    const now = Date.now()

    try {
      if (isEdit && initialStatus) {
        if (isSystem) {
          await updateStatus(initialStatus.id, {
            updatedOn: now,
            updatedBy: userId,
          })
        } else {
          const patch: Partial<StatusDocument> = {
            name: data.name.trim(),
            progressType: data.progressType,
            description: data.description ?? '',
            projectIds: data.projectIds,
            updatedOn: now,
            updatedBy: userId,
          }
          // PT change: regenerate sortOrder for the new bucket so the row
          // lands at the bottom of its destination Type group.
          if (data.progressType !== initialStatus.progressType) {
            const sameBucket = allStatuses.filter(
              (s) =>
                s.id !== initialStatus.id &&
                s.progressType === data.progressType,
            )
            const lastInBucket = sameBucket.at(-1)
            patch.sortOrder = lastInBucket
              ? generateNextRank(lastInBucket.sortOrder)
              : generateInitialRank()
          }
          await updateStatus(initialStatus.id, patch)
        }
      } else {
        // Place the new status at the end of its Progress Type bucket.
        const sameBucket = allStatuses.filter(
          (s) => s.progressType === data.progressType,
        )
        const lastInBucket = sameBucket.at(-1)
        const sortOrder = lastInBucket
          ? generateNextRank(lastInBucket.sortOrder)
          : generateInitialRank()

        await createStatus({
          id: uuidv4(),
          envId: workspaceId,
          projectIds: data.projectIds,
          name: data.name.trim(),
          progressType: data.progressType,
          description: data.description ?? '',
          sortOrder,
          isSystemGenerated: false,
          isDefault: false,
          isDeleted: false,
          createdOn: now,
          createdBy: userId,
          updatedOn: now,
          updatedBy: userId,
        })
      }
      toast.success(t('status_management.toast.saved'))
      onOpenChange(false)
    } catch (error) {
      logger.logError(error as Error, LogCategory.API_ERROR, {
        component: 'StatusFormDialog',
        action: isEdit ? 'update_status' : 'create_status',
      })
      toast.error(t('status_management.toast.save_failed'))
    }
  }

  const lockNonSortFields = isSystem
  const isSubmitting = form.formState.isSubmitting

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {isEdit
              ? t('status_management.dialog.edit_title')
              : t('status_management.dialog.create_title')}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? t('status_management.dialog.edit_description')
              : t('status_management.dialog.create_description')}
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-4"
          noValidate
        >
          <div className="space-y-2">
            <Label htmlFor="status-name">
              {t('status_management.fields.name')}
            </Label>
            <Input
              id="status-name"
              maxLength={50}
              disabled={lockNonSortFields}
              {...form.register('name')}
            />
            {form.formState.errors.name && (
              <p className="text-xs text-destructive">
                {form.formState.errors.name.message ??
                  t('status_management.errors.name_required')}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="status-progress-type">
              {t('status_management.fields.progress_type')}
            </Label>
            <Controller
              control={form.control}
              name="progressType"
              render={({ field }) => (
                <Select
                  value={field.value}
                  onValueChange={(value) => field.onChange(value as ProgressType)}
                  disabled={lockNonSortFields}
                >
                  <SelectTrigger id="status-progress-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {progressTypeOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="status-description">
              {t('status_management.fields.description')}
            </Label>
            <Textarea
              id="status-description"
              maxLength={200}
              rows={3}
              disabled={lockNonSortFields}
              {...form.register('description')}
            />
          </div>

          <div className="space-y-2">
            <Label>{t('status_management.fields.scope')}</Label>
            <Controller
              control={form.control}
              name="projectIds"
              render={({ field }) => {
                const legacyCount = field.value.filter(
                  (id) => !selectableProjectIds.has(id),
                ).length
                return (
                  <>
                    <ProjectScopePicker
                      value={field.value}
                      onChange={field.onChange}
                      projects={projects}
                      disabled={lockNonSortFields}
                    />
                    {legacyCount > 0 && !lockNonSortFields && (
                      <p className="text-xs text-muted-foreground">
                        {t('status_management.scope.legacy_count', {
                          count: legacyCount,
                        })}{' '}
                        <button
                          type="button"
                          className="underline underline-offset-2 hover:text-foreground"
                          onClick={() =>
                            field.onChange(
                              field.value.filter((id) =>
                                selectableProjectIds.has(id),
                              ),
                            )
                          }
                        >
                          {t('status_management.scope.clear_legacy')}
                        </button>
                      </p>
                    )}
                  </>
                )
              }}
            />
            <p className="text-xs text-muted-foreground">
              {t('status_management.fields.scope_hint')}
            </p>
          </div>

          {lockNonSortFields && (
            <p className="text-xs text-muted-foreground">
              {t('status_management.dialog.system_lock_notice')}
            </p>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              {t('status_management.actions.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting || !userId}>
              {isEdit
                ? t('status_management.actions.save')
                : t('status_management.actions.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

interface ProjectScopePickerProps {
  value: Array<string>
  onChange: (next: Array<string>) => void
  projects: Array<ProjectDocument>
  disabled?: boolean
}

function ProjectScopePicker({
  value,
  onChange,
  projects,
  disabled,
}: ProjectScopePickerProps) {
  const { t } = useTranslation('settings')
  const [open, setOpen] = useState(false)

  const groups = useMemo(() => groupProjectsBySpace(projects), [projects])

  const label =
    value.length === 0
      ? t('status_management.scope.all_projects')
      : t('status_management.scope.n_projects', { count: value.length })

  const toggleProject = (id: string) => {
    if (value.includes(id)) {
      onChange(value.filter((v) => v !== id))
    } else {
      onChange([...value, id])
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between"
        >
          <span className="truncate text-left">{label}</span>
          <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
      >
        <Command>
          <CommandInput
            placeholder={t('status_management.scope.search_placeholder')}
          />
          <CommandList>
            <CommandEmpty>
              {t('status_management.scope.no_projects')}
            </CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__all_projects_global__"
                onSelect={() => onChange([])}
                className="gap-2"
              >
                <Globe className="size-4" />
                <span className="flex-1">
                  {t('status_management.scope.all_projects_global')}
                </span>
                <Check
                  className={cn(
                    'size-4',
                    value.length === 0 ? 'opacity-100' : 'opacity-0',
                  )}
                />
              </CommandItem>
            </CommandGroup>
            {groups.map(({ space, children }) => {
              const headingLabel =
                space?.name ||
                (space ? 'Untitled space' : t('status_management.scope.orphan_heading'))
              const groupKey = space?.id ?? '__orphan__'
              return (
                <CommandGroup
                  key={groupKey}
                  heading={
                    <span className="flex items-center gap-1.5">
                      {space && (
                        <ProjectIcon
                          iconType={space.iconType}
                          icon={space.icon}
                          iconColor={space.iconColor}
                          size={12}
                          className="text-muted-foreground"
                        />
                      )}
                      <span>{headingLabel}</span>
                    </span>
                  }
                >
                  {children.map((project) => {
                    const selected = value.includes(project.id)
                    const projectName = project.name || 'Untitled project'
                    return (
                      <CommandItem
                        key={project.id}
                        value={`${headingLabel} ${projectName}`}
                        onSelect={() => toggleProject(project.id)}
                      >
                        <span className="flex-1 truncate">{projectName}</span>
                        <Check
                          className={cn(
                            'size-4',
                            selected ? 'opacity-100' : 'opacity-0',
                          )}
                        />
                      </CommandItem>
                    )
                  })}
                </CommandGroup>
              )
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
